import React from 'react';
import { motion } from 'motion/react';
import { Play } from 'lucide-react';
import ParticlesBackground from '../ui/ParticlesBackground';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import MockupFrame from '../ui/MockupFrame';
import FloatingCard from '../ui/FloatingCard';
import MouseParallax from '../animations/MouseParallax';
import ScrollParallax from '../animations/ScrollParallax';
import posImage from '../../assets/img/page vente light mode.png';

const cities = ['Dakar', 'Abidjan', 'Bamako', 'Conakry', 'Ouagadougou', 'Thies', 'Lome', 'Niamey', 'Cotonou', 'Douala'];

export default function HeroSection() {
  return (
    <section className="relative min-h-[80vh] lg:min-h-[90vh] bg-dark pt-20 overflow-hidden">
      {/* Particles Backdrop */}
      <ParticlesBackground />

      {/* Radial Gradient Overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 30% 50%, rgba(249,115,22,0.15) 0%, transparent 60%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center min-h-[calc(80vh-5rem)] lg:min-h-[calc(90vh-5rem)]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center w-full">
          {/* Left Column */}
          <ScrollParallax speed={-15}>
          <div>
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0 }}
            >
              <Badge variant="glass">
                🌍 Conçu en Afrique, pour l'Afrique
              </Badge>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-black mt-4 leading-tight"
            >
              <span className="gradient-text-orange">Transformez votre boutique.</span>
              <br />
              <span className="gradient-text-orange">Multipliez vos profits.</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg text-gray-400 mt-4 max-w-xl"
            >
              Chaque dette oubliée, chaque erreur de caisse, chaque rupture de stock vous coûte de l'argent. XamXamBoutik arrête l'hémorragie dès le premier mois.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-wrap gap-4 mt-5"
            >
              <Button variant="primary" size="lg" href="https://xamxamshop.com">
                Démarrer gratuitement
              </Button>
              <Button variant="outline" size="lg" href="#demo">
                <Play size={20} />
                Voir la démo
              </Button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-wrap gap-4 mt-5"
            >
              <span className="text-sm text-gray-500">✓ 1 mois d'essai gratuit</span>
              <span className="text-sm text-gray-500">✓ Sans carte bancaire</span>
              <span className="text-sm text-gray-500">✓ Support réactif par WhatsApp</span>
            </motion.div>
          </div>
          </ScrollParallax>

          {/* Right Column */}
          <ScrollParallax speed={-25} className="hidden lg:block">
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5, type: 'spring' }}
            >
              <MouseParallax>
                <div className="relative">
                  <MockupFrame
                    src={posImage}
                    alt="XamXamBoutik - Page de vente"
                    perspective={true}
                    glow={true}
                  />
                  <FloatingCard position="top-left">
                    ⚡ 30 sec par vente
                  </FloatingCard>
                  <FloatingCard position="bottom-right" delayed={true}>
                    📊 +40% de profits
                  </FloatingCard>
                </div>
              </MouseParallax>
            </motion.div>
          </ScrollParallax>
        </div>

        {/* Trust Cities Banner */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-4 pb-2 text-center"
        >
          <p className="text-xs text-gray-600 mb-3 uppercase tracking-widest">
            Utilisé par des commerçants à travers l'Afrique
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {cities.map((city) => (
              <span
                key={city}
                className="text-xs sm:text-sm text-gray-500 font-medium"
              >
                📍 {city}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
